/**
 * SceneInsightsDrawer — Voxcanon DM confidence panel (smart wrapper).
 *
 * Fetches all generated scene images for a meeting and wires the live SSE
 * invalidation, then hands the data to the dumb `SceneInsightsDrawerView` to
 * render. Updates live during pipeline execution so the DM sees what the AI is
 * detecting and generating. The view owns all presentation (and its brand-aware
 * scene-type badges); this wrapper owns data + side effects only.
 */
import { SSE_EVENTS } from "@lexicanon/shared"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import { useImageLightbox } from "../../hooks/useImageLightbox"
import { createReconnectingEventSource } from "../../hooks/useSSE"
import { fetchVisuals } from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import type { IProps, ISceneImage } from "./interfaces"
import { SceneInsightsDrawerView } from "./SceneInsightsDrawerView"

export function SceneInsightsDrawer({
  opened,
  onClose,
  meetingId,
  companyId,
  isLive = false,
}: IProps): React.JSX.Element {
  const queryClient = useQueryClient()
  const { openLightbox, LightboxModal } = useImageLightbox()

  const { data: visuals = [], isLoading } = useCompanyQuery<ISceneImage[]>({
    // Keyed by the passed-in companyId (matching the SSE invalidation below), not
    // the active org — kept byte-identical, so the org id from the wrapper is unused.
    key: () => queryKeys.sceneInsights(companyId ?? null, meetingId),
    queryFn: (company) => fetchVisuals(company, meetingId),
    enabled: opened,
    refetchInterval: isLive ? 10_000 : false,
  })

  // Live SSE updates: listen for visual_generated events and invalidate. Routes
  // through the shared reconnecting wrapper so a 5xx / pod restart re-subscribes
  // with backoff instead of silently going dead.
  useEffect(() => {
    if (!opened || !companyId) return

    const handleMessage = (event: MessageEvent<string>): void => {
      try {
        const parsed: unknown = JSON.parse(event.data)
        if (typeof parsed !== "object" || parsed === null) return
        const data = parsed as { meetingId?: string; type?: string }
        if (data.meetingId !== meetingId) return
        if (data.type === SSE_EVENTS.VISUAL_GENERATED) {
          queryClient.invalidateQueries({ queryKey: queryKeys.sceneInsights(companyId ?? null, meetingId) })
        }
      } catch {}
    }

    return createReconnectingEventSource(`/api/companies/${companyId}/events`, false, handleMessage)
  }, [opened, meetingId, companyId, queryClient])

  return (
    <SceneInsightsDrawerView
      opened={opened}
      onClose={onClose}
      visuals={visuals}
      isLoading={isLoading}
      isLive={isLive}
      onImageClick={(url, alt) => openLightbox([{ url, alt }])}
      lightboxModal={LightboxModal}
    />
  )
}
